import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ClipboardPaste, FileUp, History } from 'lucide-react';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SectionMappingContext } from './SectionMappingContext';
import { unreadableFileReason } from './sectionRevision';
import { flattenSections, useOutlineSections } from './workingOutlineHooks';
/**
 * SectionAuthoringRail — what a writer needs beside one section: the volume
 * outline (this section marked), the requirement and source references that
 * apply to it, and the ways to bring text in besides typing it.
 */ export function SectionAuthoringRail({ projectId, sectionId, busy, entryStatus, onPaste, onUploadFile, onOpenHistory }) {
    const { volumeId = '' } = useParams();
    const outline = useOutlineSections(projectId, volumeId || undefined);
    const sections = flattenSections(outline.data ?? []);
    const [pasting, setPasting] = useState(false);
    const [pasted, setPasted] = useState('');
    const [fileProblem, setFileProblem] = useState('');
    const fileRef = useRef(null);
    return /*#__PURE__*/ _jsxs("aside", {
        "data-testid": Testids.proposalVersions.rail,
        "aria-label": "Section authoring rail",
        className: "w-72 shrink-0 space-y-3 rounded-lg border border-border bg-card p-3 text-xs text-foreground",
        children: [
            /*#__PURE__*/ _jsxs("section", {
                "data-testid": Testids.proposalVersions.railOutline,
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "mb-1 font-semibold text-foreground",
                        children: "Outline"
                    }),
                    outline.isLoading ? /*#__PURE__*/ _jsx("output", {
                        children: "Loading outline…"
                    }) : outline.isError ? /*#__PURE__*/ _jsxs("div", {
                        role: "alert",
                        children: [
                            "Couldn't load the outline.",
                            ' ',
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                type: "button",
                                className: "text-brand",
                                onClick: ()=>outline.refetch(),
                                children: "Try again"
                            })
                        ]
                    }) : sections.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "This volume has no outline sections yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "max-h-40 space-y-0.5 overflow-y-auto",
                        children: sections.map((s)=>/*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "link",
                                    size: "inline",
                                    type: "button",
                                    "aria-current": s.sectionId === sectionId ? 'true' : undefined,
                                    onClick: ()=>document.getElementById(`section-${s.sectionId}`)?.scrollIntoView({
                                            behavior: 'smooth'
                                        }),
                                    className: `w-full truncate rounded px-1.5 py-0.5 text-left ${s.sectionId === sectionId ? 'bg-brand-muted font-semibold text-brand' : 'hover:bg-muted'}`,
                                    style: {
                                        paddingLeft: `${6 + Math.max(0, s.level - 1) * 10}px`
                                    },
                                    children: s.title || 'Untitled section'
                                })
                            }, s.sectionId))
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                "data-testid": Testids.proposalVersions.railSources,
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "mb-1 font-semibold text-foreground",
                        children: "Source references"
                    }),
                    volumeId ? /*#__PURE__*/ _jsx(SectionMappingContext, {
                        projectId: projectId,
                        volumeId: volumeId,
                        sectionId: sectionId
                    }) : /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Open this section from its volume to see its sources."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                "data-testid": Testids.proposalVersions.railAssist,
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-semibold text-foreground",
                        children: "Bring in text"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Type in the editor, paste text, or upload a revised file. Each way adds a new revision and keeps where it came from."
                    }),
                    pasting ? /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                "data-testid": Testids.proposalVersions.pasteInput,
                                value: pasted,
                                onChange: (e)=>setPasted(e.target.value),
                                rows: 5,
                                "aria-label": "Text to bring in",
                                placeholder: "Paste the revised text here",
                                className: "w-full rounded border border-border px-2 py-1"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                type: "button",
                                "data-testid": Testids.proposalVersions.pasteSubmit,
                                disabled: busy || pasted.trim() === '',
                                onClick: ()=>{
                                    onPaste(pasted);
                                    setPasted('');
                                    setPasting(false);
                                },
                                className: "rounded bg-muted px-2 py-1 font-medium text-primary-foreground disabled:opacity-50",
                                children: "Add as new revision"
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "link",
                        size: "inline",
                        type: "button",
                        "data-testid": Testids.proposalVersions.pasteOpen,
                        onClick: ()=>setPasting(true),
                        className: "inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted",
                        children: [
                            /*#__PURE__*/ _jsx(ClipboardPaste, {
                                className: "h-3 w-3"
                            }),
                            "Paste text"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        type: "button",
                        variant: "outline",
                        size: "sm",
                        disabled: busy,
                        onClick: ()=>fileRef.current?.click(),
                        children: [
                            /*#__PURE__*/ _jsx(FileUp, {
                                className: "h-3 w-3"
                            }),
                            "Upload revised file"
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        ref: fileRef,
                        "data-testid": Testids.proposalVersions.uploadInput,
                        type: "file",
                        accept: ".txt,.md,.markdown,.html,.htm,.docx",
                        disabled: busy,
                        className: "sr-only",
                        "aria-label": "Revised file",
                        onChange: (e)=>{
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            const reason = unreadableFileReason(file.name);
                            setFileProblem(reason);
                            if (!reason) onUploadFile(file);
                        }
                    }),
                    fileProblem && /*#__PURE__*/ _jsx("div", {
                        role: "alert",
                        className: "text-danger",
                        children: fileProblem
                    }),
                    entryStatus && /*#__PURE__*/ _jsx("output", {
                        "data-testid": Testids.proposalVersions.entryStatus,
                        className: "block text-success",
                        children: entryStatus
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "link",
                        size: "inline",
                        type: "button",
                        onClick: onOpenHistory,
                        className: "inline-flex items-center gap-1 text-brand hover:text-brand",
                        children: [
                            /*#__PURE__*/ _jsx(History, {
                                className: "h-3 w-3"
                            }),
                            "Open version history"
                        ]
                    })
                ]
            })
        ]
    });
}
